import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { CRICKET_API_URL } from "../config";
import { UserContext } from "../context/UserContext";
import { getPlayerId, normalizePlayerList } from "../utils/playerIdentity";
import { getMatchStatus, getTeamScore } from "../utils/matchHelpers";

const DEFAULT_TEAM_IMAGE = "https://h.cricapi.com/img/icon512.png";
const DEFAULT_PLAYER_IMAGE = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

const handlePlayerImageError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = DEFAULT_PLAYER_IMAGE;
};

const getStoredUserId = () => {
    try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
            const userObj = JSON.parse(userStr);
            return userObj?._id || userObj?.id || null;
        }
    } catch (error) {
        console.error("MatchDashboard user parse error:", error);
    }

    return localStorage.getItem("userId") || localStorage.getItem("_id") || null;
};

const getTeamCode = (teamName = "T1") =>
    String(teamName || "T1")
        .substring(0, 3)
        .toUpperCase();

const hasDisplayValue = (value) =>
    value !== null &&
    value !== undefined &&
    String(value).trim() !== "";

const buildDisplayTeam = (team = {}, fallbackName = "Team") => {
    const name = team.name || fallbackName;

    return {
        name,
        code: team.code || getTeamCode(name),
        image: team.image || team.logo || DEFAULT_TEAM_IMAGE,
    };
};

const getPlayerById = (players = [], playerId) =>
    (players || []).find((player) => getPlayerId(player) === String(playerId || ""));

const parseScoreValue = (value = "") => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return "";

    const scoreMatch = normalizedValue.match(/^(.*?)(?:\s*\(([\d.]+)\))?$/);
    return String(scoreMatch?.[1] || normalizedValue).trim();
};

const getRawLiveFlag = (match = {}) =>
    String(
        match?.event_live ??
        (match?.status?.isLive === true ? "1" : "0"),
    ).trim();

const getMappedScoreValue = (value) =>
    hasDisplayValue(value) ? parseScoreValue(value) : "";

const normalizeScoreTeamData = (scoreTeam = null, fallbackName = "") => {
    if (scoreTeam && typeof scoreTeam === "object" && !Array.isArray(scoreTeam)) {
        return {
            name: scoreTeam?.name || fallbackName || "",
            runs: getMappedScoreValue(scoreTeam?.runs),
        };
    }

    return {
        name: fallbackName || "",
        runs: getMappedScoreValue(scoreTeam),
    };
};

const normalizeLiveMatchData = (data = {}) => {
    if (!data || typeof data !== "object") {
        return null;
    }

    const overs = hasDisplayValue(data?.score?.overs)
        ? String(data.score.overs).trim()
        : "";
    const isLive =
        data?.isLive === true ||
        data?.status?.isLive === true ||
        getRawLiveFlag(data) === "1";
    const normalizedStatus =
        typeof data?.status === "string"
            ? String(data.status).trim().toLowerCase()
            : (isLive ? "live" : "upcoming");

    return {
        apiStatus: String(data?.apiStatus || data?.event_status || "").trim(),
        status_info: String(
            data?.status_info ||
            data?.event_status_info ||
            data?.status?.text ||
            data?.event_status ||
            "",
        ).trim(),
        event_status_info: String(
            data?.event_status_info || data?.status_info || "",
        ).trim(),
        event_home_final_result: String(data?.event_home_final_result || "").trim(),
        event_service_home: String(data?.event_service_home || "").trim(),
        event_away_final_result: String(data?.event_away_final_result || "").trim(),
        event_service_away: String(data?.event_service_away || "").trim(),
        toss: String(data?.toss || data?.event_toss || "").trim(),
        score: {
            home: normalizeScoreTeamData(data?.score?.home, data?.score?.home?.name || ""),
            away: normalizeScoreTeamData(data?.score?.away, data?.score?.away?.name || ""),
            overs,
        },
        isLive,
        event_live: isLive ? "1" : "0",
        status: {
            matchId: String(data?.matchId || data?.status?.matchId || data?.event_key || "").trim(),
            status: normalizedStatus,
            isLive,
            text: String(data?.status_info || data?.status?.text || data?.event_status || data?.status || "").trim(),
        },
    };
};

const getRoleCounts = (players = []) => {
    const counts = { WK: 0, BAT: 0, AR: 0, BOWL: 0 };

    (players || []).forEach((player) => {
        const role = String(player?.role || "").toUpperCase();

        if (role.includes("WK") || role.includes("KEEPER")) counts.WK += 1;
        else if (role.includes("BAT")) counts.BAT += 1;
        else if (role.includes("AR") || role.includes("ALL")) counts.AR += 1;
        else if (role.includes("BOWL")) counts.BOWL += 1;
    });

    return counts;
};

const MatchDashboard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { matchId: routeMatchId } = useParams();
    const { user, loading: isUserLoading } = useContext(UserContext);

    const routeMatchData = useMemo(
        () => location.state?.matchData || {},
        [location.state?.matchData],
    );
    const actualMatchId =
        routeMatchData.matchId ||
        routeMatchData._id ||
        routeMatchData.id ||
        routeMatchId;
    const currentUserId = useMemo(
        () => user?._id || user?.id || getStoredUserId(),
        [user],
    );
    const token = localStorage.getItem("token");
    const headers = useMemo(
        () => (token ? { Authorization: `Bearer ${token}` } : {}),
        [token],
    );

    useEffect(() => {
        if (!actualMatchId) {
            navigate("/home");
            return;
        }

        if (isUserLoading) {
            return;
        }

        if (!token || !currentUserId) {
            navigate("/login");
        }
    }, [actualMatchId, currentUserId, isUserLoading, navigate, token]);

    const [activeTab, setActiveTab] = useState("myContests");
    const [joinedContests, setJoinedContests] = useState([]);
    const [myTeams, setMyTeams] = useState([]);
    const [liveMatchInfo, setLiveMatchInfo] = useState(null);
    const [isAuthChecking, setIsAuthChecking] = useState(true);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isUserLoading) {
            setIsAuthChecking(true);
            return;
        }

        setIsAuthChecking(false);
    }, [isUserLoading]);

    const matchData = useMemo(
        () => ({
            ...routeMatchData,
            apiStatus:
                liveMatchInfo?.apiStatus ||
                routeMatchData?.apiStatus ||
                routeMatchData?.event_status ||
                "",
            status_info:
                liveMatchInfo?.status_info ||
                liveMatchInfo?.event_status_info ||
                routeMatchData?.status_info ||
                routeMatchData?.event_status_info ||
                "",
            event_status_info:
                liveMatchInfo?.event_status_info ||
                routeMatchData?.event_status_info ||
                routeMatchData?.status_info ||
                "",
            event_home_final_result:
                liveMatchInfo?.event_home_final_result ||
                routeMatchData?.event_home_final_result ||
                "",
            event_service_home:
                liveMatchInfo?.event_service_home ||
                routeMatchData?.event_service_home ||
                "",
            event_away_final_result:
                liveMatchInfo?.event_away_final_result ||
                routeMatchData?.event_away_final_result ||
                "",
            event_service_away:
                liveMatchInfo?.event_service_away ||
                routeMatchData?.event_service_away ||
                "",
            toss:
                liveMatchInfo?.toss ||
                routeMatchData?.toss ||
                routeMatchData?.event_toss ||
                "",
            score: {
                home: normalizeScoreTeamData(
                    liveMatchInfo?.score?.home ?? routeMatchData?.score?.home,
                    liveMatchInfo?.score?.home?.name ||
                    routeMatchData?.team1?.name ||
                    routeMatchData?.homeTeam?.name ||
                    "",
                ),
                away: normalizeScoreTeamData(
                    liveMatchInfo?.score?.away ?? routeMatchData?.score?.away,
                    liveMatchInfo?.score?.away?.name ||
                    routeMatchData?.team2?.name ||
                    routeMatchData?.awayTeam?.name ||
                    "",
                ),
                overs: liveMatchInfo?.score?.overs ?? routeMatchData?.score?.overs ?? "",
            },
            event_live:
                liveMatchInfo?.event_live ??
                routeMatchData?.event_live ??
                (liveMatchInfo?.isLive ? "1" : "0"),
        }),
        [routeMatchData, liveMatchInfo],
    );

    useEffect(() => {
        console.log("🔥 MATCH DATA:", matchData);
    }, [matchData]);

    useEffect(() => {
        if (!actualMatchId || isAuthChecking || !token || !currentUserId) return undefined;

        let isActive = true;

        const fetchPageData = async () => {
            if (isActive) {
                setLoading(true);
                setIsPageLoading(true);
            }

            try {
                const [contestRes, teamRes] = await Promise.allSettled([
                    axios.get(
                        `${CRICKET_API_URL}/api/cricket/contests/match/${actualMatchId}/joined/${currentUserId}`,
                        { headers },
                    ),
                    axios.get(
                        `${CRICKET_API_URL}/api/cricket/my-teams/${currentUserId}/${actualMatchId}`,
                        { headers },
                    ),
                ]);

                if (!isActive) return;

                if (contestRes.status === "fulfilled" && contestRes.value.data?.success) {
                    setJoinedContests(contestRes.value.data.data || []);
                } else {
                    setJoinedContests([]);
                    if (contestRes.status === "rejected") {
                        console.error("MatchDashboard joined contests error:", contestRes.reason);
                    }
                }

                if (teamRes.status === "fulfilled" && teamRes.value.data?.success) {
                    const normalizedTeams = (teamRes.value.data.data || []).map((team) => ({
                        ...team,
                        players: normalizePlayerList(team.players || []),
                    }));
                    setMyTeams(normalizedTeams);
                } else {
                    setMyTeams([]);
                    if (teamRes.status === "rejected") {
                        console.error("MatchDashboard team fetch error:", teamRes.reason);
                    }
                }
            } catch (error) {
                if (!isActive) return;

                console.error("MatchDashboard page data error:", error);
            } finally {
                if (isActive) {
                    setLoading(false);
                    setIsPageLoading(false);
                }
            }
        };

        fetchPageData();

        return () => {
            isActive = false;
        };
    }, [actualMatchId, currentUserId, headers, isAuthChecking, token]);

    useEffect(() => {
        if (!actualMatchId || isAuthChecking || isPageLoading || !token || !currentUserId) return undefined;

        let isActive = true;

        const fetchLiveScore = async () => {
            try {
                const res = await axios.get(
                    `${CRICKET_API_URL}/api/cricket/match/${actualMatchId}/live`,
                    {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                    },
                );
                const data = res?.data;
                if (!data) return;

                if (isActive) {
                    setLiveMatchInfo(normalizeLiveMatchData(data));
                }
            } catch (error) {
                if (!isActive) return;

                console.error("MatchDashboard live score error:", error);
                setIsPageLoading(false);
            }
        };

        fetchLiveScore();
        const interval = setInterval(fetchLiveScore, 15000);
        return () => {
            isActive = false;
            clearInterval(interval);
        };
    }, [actualMatchId, currentUserId, isAuthChecking, isPageLoading, token]);

    const handleContestClick = (contest) => {
        navigate(`/live-leaderboard/${contest._id}`, {
            state: {
                matchData: {
                    ...matchData,
                    matchId: actualMatchId,
                    status: liveMatchInfo?.status?.status || matchData.status,
                    status_info: matchData.status_info || null,
                },
                contest,
            },
        });
    };

    const handleViewTeam = (team) => {
        if (!team) return;

        const normalizedTeam = {
            ...team,
            players: normalizePlayerList(team.players || []),
            captainId: String(team.captainId || ""),
            viceCaptainId: String(team.viceCaptainId || team.vcId || ""),
        };
        const resolvedTeamId = normalizedTeam._id || normalizedTeam.id || null;
        const previewState = {
            selectedTeam: normalizedTeam,
            team: normalizedTeam,
            players: normalizedTeam.players,
            captainId: normalizedTeam.captainId,
            viceCaptainId: normalizedTeam.viceCaptainId,
            matchData: {
                ...matchData,
                matchId: actualMatchId,
                status: liveMatchInfo?.status?.status || matchData.status,
                status_info: matchData.status_info || null,
                team1,
                team2,
            },
        };

        if (resolvedTeamId) {
            navigate(`/team-preview/${resolvedTeamId}`, { state: previewState });
            return;
        }

        navigate("/team-preview", { state: previewState });
    };

    const teamNameParts = String(matchData.name || "").split(" vs ");
    const team1 = buildDisplayTeam(
        {
            ...(matchData.team1 || matchData.homeTeam || {}),
            name:
                matchData.team1?.name ||
                matchData.homeTeam?.name ||
                matchData.teamA ||
                teamNameParts[0],
        },
        teamNameParts[0] || "Team 1",
    );
    const team2 = buildDisplayTeam(
        {
            ...(matchData.team2 || matchData.awayTeam || {}),
            name:
                matchData.team2?.name ||
                matchData.awayTeam?.name ||
                matchData.teamB ||
                teamNameParts[1],
        },
        teamNameParts[1] || "Team 2",
    );
    const headerMatch = useMemo(
        () => ({
            ...matchData,
            status:
                liveMatchInfo?.status?.status ||
                matchData?.status?.status ||
                matchData?.status ||
                routeMatchData?.status ||
                "",
            apiStatus:
                liveMatchInfo?.apiStatus ||
                matchData?.apiStatus ||
                routeMatchData?.apiStatus ||
                liveMatchInfo?.event_status ||
                routeMatchData?.event_status ||
                "",
            status_info:
                liveMatchInfo?.status_info ||
                liveMatchInfo?.event_status_info ||
                liveMatchInfo?.status?.text ||
                matchData?.status_info ||
                matchData?.event_status_info ||
                routeMatchData?.status_info ||
                routeMatchData?.event_status_info ||
                "",
            event_status_info:
                liveMatchInfo?.event_status_info ||
                matchData?.event_status_info ||
                routeMatchData?.event_status_info ||
                "",
            toss:
                liveMatchInfo?.toss ||
                matchData?.toss ||
                routeMatchData?.toss ||
                routeMatchData?.event_toss ||
                "",
            isLive:
                liveMatchInfo?.isLive === true ||
                matchData?.isLive === true ||
                String(matchData?.event_live || "").trim() === "1",
            score: {
                home: {
                    ...matchData?.score?.home,
                    runs:
                        liveMatchInfo?.score?.home?.runs ??
                        matchData?.score?.home?.runs ??
                        "-",
                },
                away: {
                    ...matchData?.score?.away,
                    runs:
                        liveMatchInfo?.score?.away?.runs ??
                        matchData?.score?.away?.runs ??
                        "-",
                },
                overs:
                    liveMatchInfo?.score?.overs ??
                    matchData?.score?.overs ??
                    "",
            },
            event_home_final_result:
                liveMatchInfo?.event_home_final_result ||
                matchData?.event_home_final_result ||
                routeMatchData?.event_home_final_result ||
                "",
            event_service_home:
                liveMatchInfo?.event_service_home ||
                matchData?.event_service_home ||
                routeMatchData?.event_service_home ||
                "",
            event_away_final_result:
                liveMatchInfo?.event_away_final_result ||
                matchData?.event_away_final_result ||
                routeMatchData?.event_away_final_result ||
                "",
            event_service_away:
                liveMatchInfo?.event_service_away ||
                matchData?.event_service_away ||
                routeMatchData?.event_service_away ||
                "",
        }),
        [liveMatchInfo, matchData, routeMatchData],
    );
    const status = getMatchStatus(headerMatch);
    const isCancelledMatch = status.isCancelled;
    const hasHeaderMatchData = Boolean(
        actualMatchId &&
        (
            headerMatch?.score?.home?.name ||
            headerMatch?.score?.away?.name ||
            routeMatchData?.team1?.name ||
            routeMatchData?.team2?.name ||
            routeMatchData?.homeTeam?.name ||
            routeMatchData?.awayTeam?.name ||
            routeMatchData?.teamA ||
            routeMatchData?.teamB ||
            routeMatchData?.name
        ),
    );
    const isHeaderLoading = loading || !hasHeaderMatchData;
    const displayHomeScore = getTeamScore(headerMatch, "home");
    const displayAwayScore = getTeamScore(headerMatch, "away");
    const headerStatusText = status.info || "Match not started";
    const headerCardStyle = isCancelledMatch
        ? {
            background: "#f8f9fa",
            border: "1px solid #d6d9dc",
            boxShadow: "0 10px 24px rgba(108, 117, 125, 0.18)",
        }
        : {};
    const mobileScoreCardStyle = {
        maxWidth: "350px",
        ...(isCancelledMatch
            ? {
                background: "linear-gradient(145deg, #f8f9fa, #eef1f4)",
                border: "1px solid #d6d9dc",
                boxShadow: "0 8px 20px rgba(108, 117, 125, 0.16)",
            }
            : {}),
    };

    useEffect(() => {
        console.log("🏏 UI SCORE:", displayHomeScore, displayAwayScore, headerStatusText);
    }, [displayAwayScore, displayHomeScore, headerStatusText]);

    if (isPageLoading || isAuthChecking) {
        return (
            <div className="container-fluid p-3" style={{ background: "#f3f4f6", minHeight: "100vh" }}>
                <div
                    className="card border-0 mb-3 overflow-hidden"
                    style={{
                        height: "150px",
                        borderRadius: "20px",
                        background: "linear-gradient(135deg, #e5e7eb 0%, #f3f4f6 100%)",
                        animation: "matchDashboardPulse 1.5s infinite ease-in-out",
                    }}
                ></div>

                <div className="d-flex gap-3 mb-3">
                    <div
                        style={{
                            height: "40px",
                            width: "50%",
                            background: "#e2e8f0",
                            borderRadius: "10px",
                            animation: "matchDashboardPulse 1.5s infinite ease-in-out",
                        }}
                    ></div>
                    <div
                        style={{
                            height: "40px",
                            width: "50%",
                            background: "#e2e8f0",
                            borderRadius: "10px",
                            animation: "matchDashboardPulse 1.5s infinite ease-in-out",
                        }}
                    ></div>
                </div>

                <div
                    className="card border-0 p-3"
                    style={{
                        height: "300px",
                        borderRadius: "20px",
                        background: "linear-gradient(135deg, #e2e8f0 0%, #f8fafc 100%)",
                        animation: "matchDashboardPulse 1.5s infinite ease-in-out",
                    }}
                ></div>

                <style>
                    {`
                        @keyframes matchDashboardPulse {
                            0% { opacity: 1; }
                            50% { opacity: 0.5; }
                            100% { opacity: 1; }
                        }
                    `}
                </style>
            </div>
        );
    }

    return (
        <div style={{ minHeight: "100vh", backgroundColor: "#f3f4f6", paddingBottom: "70px" }}>
            <div
                className="sticky-top shadow-sm"
                style={{ zIndex: 1040, background: "linear-gradient(135deg, #6d0000 0%, #9c1a1a 100%)" }}
            >
                <div className="d-flex align-items-center px-3 pt-3 pb-2 text-white">
                    <i
                        className="bi bi-arrow-left fs-5 me-3"
                        onClick={() => navigate(-1)}
                        style={{ cursor: "pointer" }}
                    ></i>
                    <div className="fw-bold flex-grow-1 text-center" style={{ fontSize: "14px", letterSpacing: "0.4px" }}>
                        {matchData.tournament || "Match Dashboard"}
                    </div>
                    <div style={{ width: "20px" }}></div>
                </div>

                <div className="px-3 pb-3">
                    {isHeaderLoading ? (
                        <div
                            className="rounded-4 p-3"
                            style={{
                                background: "linear-gradient(135deg, #f8fafc 0%, #e5e7eb 100%)",
                                border: "1px solid #e5e7eb",
                                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
                                animation: "headerPulse 1.5s infinite ease-in-out",
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="rounded-pill" style={{ width: "110px", height: "12px", background: "#dbe4ee" }}></div>
                                <div className="rounded-pill" style={{ width: "94px", height: "30px", background: "#dbe4ee" }}></div>
                            </div>
                            <div className="d-none d-md-flex justify-content-center align-items-center gap-5 text-center">
                                {[0, 1].map((index) => (
                                    <div key={index} className="d-flex flex-column align-items-center">
                                        <div className="rounded-circle mb-2" style={{ width: "50px", height: "50px", background: "#dbe4ee" }}></div>
                                        <div className="rounded-pill mb-2" style={{ width: "110px", height: "14px", background: "#dbe4ee" }}></div>
                                        <div className="rounded-pill" style={{ width: "90px", height: "18px", background: "#dbe4ee" }}></div>
                                    </div>
                                ))}
                            </div>
                            <div className="d-flex d-md-none flex-column gap-3 rounded-3 p-3 mx-auto" style={{ maxWidth: "350px", background: "#ffffff" }}>
                                {[0, 1].map((index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center gap-3">
                                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                                            <div className="rounded-circle" style={{ width: "30px", height: "30px", background: "#dbe4ee" }}></div>
                                            <div className="rounded-pill" style={{ width: "70px", height: "12px", background: "#dbe4ee" }}></div>
                                        </div>
                                        <div className="rounded-pill" style={{ width: "72px", height: "14px", background: "#dbe4ee" }}></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-4 shadow-sm p-3" style={headerCardStyle}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <small className="text-muted fw-bold text-uppercase" style={{ letterSpacing: "0.6px", fontSize: "10px" }}>
                                    {matchData.tournament || "Live Score"}
                                </small>
                                <span
                                    className={`badge rounded-pill px-3 py-2 ${status.bg}`}
                                    style={status.text === "LIVE" ? { animation: "blink 1.5s infinite" } : {}}
                                >
                                    {status.text === "LIVE" ? `● ${status.text}` : status.text}
                                </span>
                            </div>

                            <div className="px-1 pb-1">
                                <div className="d-none d-md-flex justify-content-center align-items-center gap-5 text-center">
                                    <div>
                                        <img
                                            src={team1.image}
                                            alt={team1.code}
                                            width="50"
                                            height="50"
                                            className="rounded-circle mb-2 object-fit-cover shadow-sm bg-light"
                                        />
                                        <h6 className="fw-bold mb-1 text-dark">{team1.name}</h6>
                                        <div className="fw-bold fs-5 text-dark">{displayHomeScore}</div>
                                    </div>

                                    <div className="fw-bolder  text-muted px-4">VS</div>

                                    <div>
                                        <img
                                            src={team2.image}
                                            alt={team2.code}
                                            width="50"
                                            height="50"
                                            className="rounded-circle mb-2 object-fit-cover shadow-sm bg-light"
                                        />
                                        <h6 className="fw-bold mb-1 text-dark">{team2.name}</h6>
                                        <div className="fw-bold fs-5 text-dark">{displayAwayScore}</div>
                                    </div>
                                </div>

                                <div
                                    className="d-flex d-md-none flex-column gap-3 bg-white rounded-3 p-3 shadow-sm mx-auto"
                                    style={mobileScoreCardStyle}
                                >
                                    <div className="d-flex justify-content-between align-items-center gap-3">
                                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                            <img
                                                src={team1.image}
                                                alt={team1.code}
                                                width="30"
                                                height="30"
                                                className="rounded-circle object-fit-cover bg-light flex-shrink-0"
                                            />
                                            <span className="fw-bold fs-6 text-truncate">{team1.code || "T1"}</span>
                                        </div>
                                        <span className="text-dark fw-bold text-end">{displayHomeScore}</span>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center gap-3">
                                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                            <img
                                                src={team2.image}
                                                alt={team2.code}
                                                width="30"
                                                height="30"
                                                className="rounded-circle object-fit-cover bg-light flex-shrink-0"
                                            />
                                            <span className="fw-bold fs-6 text-truncate">{team2.code || "T2"}</span>
                                        </div>
                                        <span className="text-dark fw-bold text-end">{displayAwayScore}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="text-center text-muted mt-3" style={{ fontSize: "12px" }}>
                                {headerStatusText}
                            </div>
                        </div>
                    )}
                </div>

                <div className="d-flex bg-black bg-opacity-10 text-center">
                    {[
                        { key: "myContests", label: `My Contest (${joinedContests.length})` },
                        { key: "teams", label: `Teams (${myTeams.length})` },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            className={`flex-grow-1 border-0 py-3 fw-bold bg-transparent ${activeTab === tab.key ? "text-white" : "text-white-50"}`}
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                fontSize: "13px",
                                letterSpacing: "0.3px",
                                borderBottom: activeTab === tab.key ? "3px solid white" : "3px solid transparent",
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="container-fluid p-3">
                {loading ? (
                    <div className="text-center py-5 mt-5">
                        <div className="spinner-border text-danger" role="status"></div>
                    </div>
                ) : (
                    <>
                        {activeTab === "myContests" && (
                            <>
                                {/* Section Header */}
                                <div className="d-flex align-items-center mb-4 px-1">
                                    <div className="rounded" style={{ width: "6px", height: "28px", backgroundColor: "#800000" }}></div>
                                    <div className="ms-3">
                                        <h5 className="fw-bolder text-dark mb-0" style={{ letterSpacing: "0.5px" }}>Joined Contests</h5>
                                        <div className="text-muted mt-1" style={{ fontSize: "12px", fontWeight: "500" }}>
                                            Monitor your rankings and prize zones.
                                        </div>
                                    </div>
                                </div>

                                {joinedContests.length > 0 ? joinedContests.map((contest) => {
                                    const totalSpots = contest.totalSpots || 0;
                                    const remainingSpots = contest.remainingSpots || 0;
                                    const filledPercent =
                                        totalSpots > 0
                                            ? ((totalSpots - remainingSpots) / totalSpots) * 100
                                            : 0;
                                    const firstPrize =
                                        contest.prizeBreakup?.[0]?.prizeAmount ||
                                        contest.prizePool ||
                                        0;

                                    return (
                                        <div
                                            key={contest._id}
                                            className="card border-0 mb-4 position-relative overflow-hidden"
                                            style={{
                                                borderRadius: "20px",
                                                cursor: "pointer",
                                                background: "#ffffff",
                                                boxShadow: "0 8px 24px rgba(128, 0, 0, 0.08)",
                                                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = "translateY(-4px)";
                                                e.currentTarget.style.boxShadow = "0 12px 28px rgba(128, 0, 0, 0.15)";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = "translateY(0)";
                                                e.currentTarget.style.boxShadow = "0 8px 24px rgba(128, 0, 0, 0.08)";
                                            }}
                                            onClick={() => handleContestClick(contest)}
                                        >
                                            {/* Top Accent Line */}
                                            <div className="position-absolute top-0 start-0 w-100" style={{ height: "4px", background: "linear-gradient(90deg, #800000, #d9534f)" }}></div>

                                            <div className="card-body p-4">
                                                {/* Title & Open Button Row */}
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <div className="badge bg-light text-secondary border border-light-subtle rounded-pill px-3 py-2 fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                                                        {contest.name || "CONTEST"}
                                                    </div>
                                                    <button
                                                        className="btn btn-sm rounded-pill fw-bold px-4 shadow-sm"
                                                        style={{
                                                            border: "1px solid #800000",
                                                            color: "#800000",
                                                            backgroundColor: "white",
                                                            transition: "all 0.2s ease"
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = "#800000";
                                                            e.currentTarget.style.color = "white";
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = "white";
                                                            e.currentTarget.style.color = "#800000";
                                                        }}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleContestClick(contest);
                                                        }}
                                                    >
                                                        OPEN
                                                    </button>
                                                </div>

                                                {/* Prize Pool Info */}
                                                <div className="mb-4">
                                                    <div className="text-muted fw-semibold mb-1" style={{ fontSize: "12px" }}>Prize Pool</div>
                                                    <div className="fw-black text-dark mb-2" style={{ fontSize: "28px", fontWeight: "900", lineHeight: "1" }}>
                                                        ₹{contest.prizePool || 0}
                                                    </div>
                                                    <div className="d-inline-flex align-items-center badge rounded-pill px-3 py-2" style={{ backgroundColor: "#fff8e6", color: "#b38600", border: "1px solid #ffe699" }}>
                                                        <i className="bi bi-trophy-fill me-2"></i>
                                                        <span style={{ fontSize: "12px", fontWeight: "700" }}>1st Prize: ₹{firstPrize}</span>
                                                    </div>
                                                </div>

                                                {/* Progress Bar Section */}
                                                <div className="p-3 rounded-4" style={{ backgroundColor: "#f8f9fa" }}>
                                                    <div className="d-flex justify-content-between align-items-end mb-2">
                                                        <span className="text-dark fw-bold" style={{ fontSize: "13px" }}>
                                                            {Math.max(totalSpots - remainingSpots, 0)} <span className="text-muted fw-medium">Joined</span>
                                                        </span>
                                                        <span className="text-dark fw-bold" style={{ fontSize: "13px" }}>
                                                            {totalSpots} <span className="text-muted fw-medium">Spots</span>
                                                        </span>
                                                    </div>

                                                    <div className="progress rounded-pill mb-1" style={{ height: "8px", backgroundColor: "#e9ecef" }}>
                                                        <div
                                                            className="progress-bar rounded-pill progress-bar-striped progress-bar-animated"
                                                            style={{
                                                                width: `${filledPercent}%`,
                                                                background: "linear-gradient(90deg, #800000, #cc0000)"
                                                            }}
                                                        ></div>
                                                    </div>
                                                </div>

                                                {/* Footer Notes */}
                                                <div className="mt-3 pt-3 border-top border-light d-flex justify-content-center align-items-center gap-3 text-muted fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                                                    <span className="d-flex align-items-center"><i className="bi bi-wallet2 me-1"></i> Prize Pool</span>
                                                    <span style={{ color: "#dee2e6" }}>•</span>
                                                    <span className="d-flex align-items-center"><i className="bi bi-bar-chart-fill me-1"></i> Leaderboard</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    /* Premium Empty State */
                                    <div className="d-flex flex-column align-items-center justify-content-center py-5 bg-white border-0" style={{ borderRadius: "20px", boxShadow: "0 8px 24px rgba(149, 157, 165, 0.1)", minHeight: "320px" }}>
                                        <div className="rounded-circle d-flex justify-content-center align-items-center mb-4 position-relative" style={{ width: "90px", height: "90px", backgroundColor: "#fff5f5" }}>
                                            <i className="bi bi-trophy text-danger opacity-75" style={{ fontSize: "40px" }}></i>
                                            <div className="position-absolute bottom-0 end-0 bg-white rounded-circle p-1 shadow-sm">
                                                <i className="bi bi-plus-circle-fill text-danger fs-5"></i>
                                            </div>
                                        </div>
                                        <h5 className="fw-bolder text-dark mb-2">No Contests Joined</h5>
                                        <p className="text-muted text-center px-4" style={{ maxWidth: "350px", fontSize: "14px", lineHeight: "1.5" }}>
                                            You haven't participated in any contests for this match yet. Explore the available contests and win big!
                                        </p>
                                    </div>
                                )}
                            </>
                        )}

                        {activeTab === "teams" && (
                            <>
                                {/* Section Header */}
                                <div className="d-flex align-items-center mb-4 px-1">
                                    <div className="rounded" style={{ width: "6px", height: "28px", backgroundColor: "#00c853" }}></div>
                                    <div className="ms-3">
                                        <h5 className="fw-bolder text-dark mb-0" style={{ letterSpacing: "0.5px" }}>My Teams</h5>
                                        <div className="text-muted fw-bold mt-1" style={{ fontSize: "12px" }}>
                                            View your fantasy squads.
                                        </div>
                                    </div>
                                </div>

                                {myTeams.length > 0 ? myTeams.map((team, index) => {
                                    const captainPlayer = getPlayerById(team.players, team.captainId);
                                    const viceCaptainPlayer = getPlayerById(team.players, team.viceCaptainId);
                                    const roleCounts = getRoleCounts(team.players);

                                    return (
                                        <div
                                            key={team._id || index}
                                            className="card border-0 mb-4 position-relative overflow-hidden"
                                            style={{
                                                borderRadius: "20px",
                                                cursor: "pointer",
                                                background: "#ffffff",
                                                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.06)",
                                                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                            }}
                                            onClick={() => handleViewTeam(team)}
                                        >
                                            {/* Top Accent Line */}
                                            <div className="position-absolute top-0 start-0 w-100" style={{ height: "4px", background: "linear-gradient(90deg, #00c853, #69f0ae)" }}></div>

                                            <div className="card-body p-3 p-sm-4">
                                                {/* Header */}
                                                <div className="d-flex justify-content-between align-items-center mb-3 mb-sm-4">
                                                    <div className="pe-2">
                                                        <div className="fw-bolder text-dark mb-1 text-truncate" style={{ fontSize: "15px", letterSpacing: "0.3px" }}>
                                                            {team.teamName || `Team ${team.teamNumber || index + 1}`}
                                                        </div>
                                                        <div className="badge bg-light text-secondary border border-light-subtle rounded-pill px-2 py-1 fw-semibold" style={{ fontSize: "9px", letterSpacing: "0.5px" }}>
                                                            FANTASY SQUAD
                                                        </div>
                                                    </div>

                                                    <div className="d-flex align-items-center gap-1 gap-sm-2 flex-shrink-0">
                                                        <span className="badge bg-dark text-white rounded-pill px-2 px-sm-3 py-1 py-sm-2 shadow-sm d-flex align-items-center">
                                                            <i className="bi bi-people-fill me-1" style={{ fontSize: "11px" }}></i>
                                                            {(team.players || []).length}/11
                                                        </span>
                                                        <span className="badge rounded-pill px-2 px-sm-3 py-1 py-sm-2 shadow-sm d-flex align-items-center" style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" }}>
                                                            VIEW <i className="bi bi-arrow-right-short ms-1" style={{ fontSize: "14px" }}></i>
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Captain / Vice Captain */}
                                                <div className="row g-2 g-sm-3 mb-3 mb-sm-4">
                                                    {/* Captain */}
                                                    <div className="col-6">
                                                        <div className="p-2 p-sm-3 h-100 position-relative" style={{ borderRadius: "14px", background: "linear-gradient(145deg, #f1f8e9, #ffffff)", border: "1px solid #dcedc8" }}>
                                                            <div className="position-absolute top-0 end-0 mt-1 me-1 mt-sm-2 me-sm-2">
                                                                <span className="badge rounded-circle bg-success text-white fw-bold d-flex align-items-center justify-content-center shadow-sm" style={{ width: "20px", height: "20px", fontSize: "9px", padding: 0 }}>
                                                                    C
                                                                </span>
                                                            </div>
                                                            <div className="text-success fw-bold mb-2 mb-sm-3" style={{ fontSize: "9px", letterSpacing: "0.5px" }}>
                                                                CAPTAIN
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="rounded-circle p-1 bg-white shadow-sm flex-shrink-0" style={{ border: "2px solid #00e676" }}>
                                                                    <div className="rounded-circle overflow-hidden bg-light" style={{ width: "34px", height: "34px" }}>
                                                                        <img
                                                                            src={captainPlayer?.image || DEFAULT_PLAYER_IMAGE}
                                                                            alt={captainPlayer?.name || "Captain"}
                                                                            onError={handlePlayerImageError}
                                                                            className="w-100 h-100 object-fit-cover"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                {/* minWidth: 0 is important for text-truncate in flexbox */}
                                                                <div className="overflow-hidden" style={{ minWidth: 0 }}>
                                                                    <div className="fw-bold text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                                                        {captainPlayer?.name || "Not Selected"}
                                                                    </div>
                                                                    <div className="text-secondary mt-1 text-truncate" style={{ fontSize: "10px", fontWeight: "600" }}>
                                                                        {captainPlayer?.role || "BAT"}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Vice Captain */}
                                                    <div className="col-6">
                                                        <div className="p-2 p-sm-3 h-100 position-relative" style={{ borderRadius: "14px", background: "linear-gradient(145deg, #e3f2fd, #ffffff)", border: "1px solid #bbdefb" }}>
                                                            <div className="position-absolute top-0 end-0 mt-1 me-1 mt-sm-2 me-sm-2">
                                                                <span className="badge rounded-circle bg-primary text-white fw-bold d-flex align-items-center justify-content-center shadow-sm" style={{ width: "20px", height: "20px", fontSize: "9px", padding: 0 }}>
                                                                    VC
                                                                </span>
                                                            </div>
                                                            <div className="text-primary fw-bold mb-2 mb-sm-3" style={{ fontSize: "9px", letterSpacing: "0.5px" }}>
                                                                VICE CAPTAIN
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="rounded-circle p-1 bg-white shadow-sm flex-shrink-0" style={{ border: "2px solid #2196f3" }}>
                                                                    <div className="rounded-circle overflow-hidden bg-light" style={{ width: "34px", height: "34px" }}>
                                                                        <img
                                                                            src={viceCaptainPlayer?.image || DEFAULT_PLAYER_IMAGE}
                                                                            alt={viceCaptainPlayer?.name || "Vice Captain"}
                                                                            onError={handlePlayerImageError}
                                                                            className="w-100 h-100 object-fit-cover"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                {/* minWidth: 0 is important for text-truncate in flexbox */}
                                                                <div className="overflow-hidden" style={{ minWidth: 0 }}>
                                                                    <div className="fw-bold text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                                                        {viceCaptainPlayer?.name || "Not Selected"}
                                                                    </div>
                                                                    <div className="text-secondary mt-1 text-truncate" style={{ fontSize: "10px", fontWeight: "600" }}>
                                                                        {viceCaptainPlayer?.role || "BAT"}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Roles - Single Line Fix */}
                                                <div className="pt-2 pt-sm-3 border-top border-light">
                                                    <div className="d-flex flex-nowrap justify-content-between justify-content-sm-center gap-1 gap-sm-2 w-100 overflow-x-auto pb-1" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                                                        {Object.entries(roleCounts).map(([role, count]) => (
                                                            <div
                                                                key={role}
                                                                className="d-flex align-items-center px-2 py-1 bg-light rounded-pill border border-light-subtle flex-shrink-0"
                                                                style={{ transition: "all 0.2s ease" }}
                                                            >
                                                                <span className="text-secondary fw-bold me-1" style={{ fontSize: "10px", letterSpacing: "0.2px" }}>
                                                                    {role.toUpperCase()}
                                                                </span>
                                                                <span className="badge bg-dark rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ width: "18px", height: "18px", fontSize: "10px", padding: 0 }}>
                                                                    {count}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    );
                                }) : (
                                    /* Premium Empty State */
                                    <div
                                        className="d-flex flex-column align-items-center justify-content-center py-5 bg-white border-0"
                                        style={{ borderRadius: "20px", boxShadow: "0 8px 24px rgba(0,0,0,0.06)", minHeight: "320px" }}
                                    >
                                        <div className="rounded-circle d-flex justify-content-center align-items-center mb-4 position-relative" style={{ width: "90px", height: "90px", backgroundColor: "#e8f5e9" }}>
                                            <i className="bi bi-shield-shaded text-success opacity-75" style={{ fontSize: "40px" }}></i>
                                            <div className="position-absolute bottom-0 end-0 bg-white rounded-circle p-1 shadow-sm">
                                                <i className="bi bi-exclamation-circle-fill text-warning fs-5"></i>
                                            </div>
                                        </div>
                                        <h5 className="fw-bolder text-dark mb-2">No Teams Created</h5>
                                        <p className="text-muted text-center px-4" style={{ maxWidth: "350px", fontSize: "14px", lineHeight: "1.5" }}>
                                            Looks like you haven't built your squad for this match yet. Create your winning team now!
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>

            <style>
                {`
                    @keyframes blink { 0% { opacity: 1; } 50% { opacity: 0.4; } 100% { opacity: 1; } }
                    @keyframes headerPulse { 0% { opacity: 1; } 50% { opacity: 0.55; } 100% { opacity: 1; } }
                    .match-header-score {
                        font-size: 24px;
                        line-height: 1.1;
                    }
                    .match-header-vs {
                        min-width: 90px;
                    }
                    @media (max-width: 768px) {
                        .match-header {
                            flex-direction: column;
                            text-align: center;
                            gap: 10px;
                        }
                        .match-header-team {
                            width: 100%;
                        }
                        .team {
                            margin-bottom: 10px;
                        }
                        .match-header-team .d-flex {
                            justify-content: center !important;
                        }
                        .match-header-team .text-start {
                            text-align: center !important;
                        }
                        .match-header-vs {
                            min-width: auto;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default MatchDashboard;
